import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, FoTable, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { SelectField, useOsResource } from "./osForm";
import { rateCardDraft, rateCardFieldList, rateCardPatch } from "./osRules";

const money = (value) => `${formatEgp(value)} EGP`;

const showRate = (field, value) => {
  if (value == null || value === "") return "—";
  if (field?.unit === "x") return `${value}×`;
  if (field?.unit === "pcs") return String(value);
  return formatEgp(value);
};

const overrideCount = (brand) => Object.values(brand?.overrides || {}).filter((value) => value != null && value !== "").length;

const netHandling = (brand) => brand?.netFulfillmentFee ?? brand?.chargedHandlingFee ?? brand?.chainzFee;

export default function BrandRateCard() {
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const view = useOsResource((auth) => os.brandFees(auth, { status: status || undefined }), [status]);
  const brands = Array.isArray(view.data?.brands) ? view.data.brands : [];
  const fields = rateCardFieldList(view.data);
  const groups = fields.reduce((list, field) => {
    const current = list[list.length - 1];
    if (!current || current.name !== field.group) list.push({ name: field.group, fields: [field] });
    else current.fields.push(field);
    return list;
  }, []);

  const open = (row) => {
    setEditing(row);
    setDraft(rateCardDraft(row, fields));
  };

  const setValue = (key, value) => {
    setDraft((current) => ({ ...current, [key]: { value, inherit: false } }));
  };

  const inherit = (key) => {
    const resolved = editing?.[key];
    setDraft((current) => ({
      ...current,
      [key]: { value: resolved == null ? "" : String(resolved), inherit: true },
    }));
  };

  const save = async () => {
    const result = rateCardPatch(editing, draft, fields.map((field) => field.key));
    if (result.error) {
      notification.error({ message: result.error });
      return;
    }
    if (!Object.keys(result.body).length) {
      setEditing(null);
      return;
    }
    setBusy(true);
    try {
      await os.updateBrandRates(view.token, editing.vendorId, result.body);
      notification.success({ message: "Rate card saved. The next settlement uses these rates." });
      setEditing(null);
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <FoCard title="Rate card" note="Every fee uses this sheet. A blank override inherits the tier or the common fee. Saving writes the audit log and does not create a transaction. The brand needs a tier assignment first.">
      <div className="fo-tools">
        <SelectField label="Status" value={status} onChange={setStatus} allowEmpty options={["Active", "Due", "Overdue"]} />
      </div>
      <FoTable
        loading={view.loading}
        dataSource={brands}
        rowKey="vendorId"
        pagination={{ pageSize: 10, total: brands.length }}
        empty="No brand rate cards yet."
        minWidth={1180}
        columns={[
          { title: "Brand", dataIndex: "brandName" },
          { title: "Tier", dataIndex: "tierName", render: (value) => statusBadge(value || "—") },
          { title: "Net handling", key: "net", render: (_, row) => money(netHandling(row)) },
          { title: "Ship ×", dataIndex: "shippingMultiplier", render: (value) => (value == null ? "—" : `${value}×`) },
          { title: "Storage", dataIndex: "storageFee", render: (value) => formatEgp(value) },
          { title: "Overrides", key: "overrides", render: (_, row) => overrideCount(row) },
          { title: "Outstanding", dataIndex: "outstanding", render: (value) => money(value) },
          { title: "Wallet", dataIndex: "walletDue", render: (value) => money(value) },
          { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
          {
            title: "",
            key: "actions",
            render: (_, row) => (
              <button type="button" className="fo-btn" onClick={() => open(row)}>Edit rates</button>
            ),
          },
        ]}
      />
      <FoDialog open={Boolean(editing)} title={editing ? `Rates · ${editing.brandName}` : ""} onClose={() => !busy && setEditing(null)} wide>
        {editing && (
          <div className="fo-rate-meta">
            <span>Net handling <b>{money(netHandling(editing))}</b></span>
            <span>Tier fee <b>{money(editing.tierChainzFee ?? editing.tier?.chainzFee)}</b></span>
            <span>Overstock <b>{money(editing.overstockFee ?? editing.storageFee)}</b></span>
          </div>
        )}
        {groups.map((group) => (
          <div key={group.name}>
            <div className="fo-rate-group">{group.name}</div>
            <div className="fo-form-grid">
              {group.fields.map((field) => {
                const entry = draft[field.key] || { value: "", inherit: true };
                const inheriting = entry.inherit || entry.value === "";
                const storedOverride = editing?.overrides?.[field.key];
                const placeholder = !inheriting
                  ? ""
                  : entry.inherit && storedOverride != null && storedOverride !== ""
                    ? "Default"
                    : showRate(field, editing?.[field.key]);
                return (
                  <div key={field.key} className="fo-field">
                    <span>{field.label}</span>
                    <input
                      className="fo-control"
                      type="number"
                      min="0"
                      step="any"
                      value={inheriting ? "" : entry.value}
                      placeholder={placeholder}
                      onChange={(event) => setValue(field.key, event.target.value)}
                    />
                    <em>{inheriting ? "Inherited" : "Override"}{field.hint ? ` · ${field.hint}` : ""}</em>
                    <button type="button" className="fo-btn" disabled={busy || inheriting} onClick={() => inherit(field.key)}>Inherit</button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <p className="fo-note">Empty means inherit. An explicit amount is stored for this brand and used the next time that fee is charged. Additional fees with no unit price use the matching field here.</p>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={save}>Save rates</button>
        </div>
      </FoDialog>
    </FoCard>
  );
}
